'use client'

import Image from 'next/image'

import ZAPDetailButton from './z-ap-detail-button'
import { useAPDetailGender } from './z-ap-detail-gender-provider'

/**
 * Props for the ZAPDetailHero component.
 */
interface IZAPDetailHeroProps {
	/** Program name (e.g., 'Medante Balance', 'Medante Mini', 'Medante Vital') */
	programName: string
	/** Program description text */
	description: string
	/** Female program button text */
	femaleButtonText: string
	/** Male program button text */
	maleButtonText: string
	/** Hero image configuration */
	image: {
		/** Image source URL (CDN path) */
		src: string
		/** Image alt text */
		alt: string
		/** Image height in pixels */
		height: number
		/** Image width in pixels */
		width: number
	}
}

/**
 * Universal hero section for annual program details.
 * Displays the main heading, description, hero image, and gender selection buttons.
 * Automatically scrolls to program details when a gender is selected.
 * Shared across all annual program detail pages (Balance, Mini, Vital) with configurable content.
 *
 * @param programName - Name of the program (e.g., 'Medante Balance')
 * @param description - Program description paragraph
 * @param femaleButtonText - Text for female program button
 * @param maleButtonText - Text for male program button
 * @param image - Hero image configuration (src, alt, dimensions)
 *
 * @example
 * ```typescript
 * <ZAPDetailHero
 *   programName='Medante Balance'
 *   description='Starajte sa o svoje zdravie celoročne...'
 *   femaleButtonText='Medante Balance pre ženu'
 *   maleButtonText='Medante Balance pre muža'
 *   image={{
 *     src: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/annual-programs/girl-balance-on-wood.webp`,
 *     alt: 'Žena a muž v prírode',
 *     height: 382,
 *     width: 625
 *   }}
 * />
 * ```
 */
export default function ZAPDetailHero({
	programName,
	description,
	femaleButtonText,
	maleButtonText,
	image
}: IZAPDetailHeroProps) {
	const { setGender, handleScrollTo } = useAPDetailGender()

	/**
	 * Handles gender selection button click.
	 * Sets the selected gender and scrolls to program details section.
	 *
	 * @param gender - Selected gender ('male' or 'female')
	 */
	const handleClick = (gender: 'male' | 'female'): void => {
		setGender(gender)
		handleScrollTo('detail-programu')
	}

	return (
		<section className='wrapper2 mgt-25lg mgt-60 mgb-10lg mgb-90 medante-vital__hero'>
			<div className='medante-vital__hero_content'>
				<h4>Ročný program </h4>
				<h1>{programName}</h1>

				<p dangerouslySetInnerHTML={{ __html: description }} />

				<div className='medante-vital__hero_buttons'>
					<ZAPDetailButton
						text={femaleButtonText}
						onClick={() => handleClick('female')}
						isPrimary={true}
						color='cyclamen'
						className='!min-w-[218px]'
					/>

					<ZAPDetailButton
						text={maleButtonText}
						onClick={() => handleClick('male')}
						isPrimary={true}
						color='blue'
						className='!min-w-[218px]'
					/>
				</div>
			</div>

			<div className='medante-vital__hero_image'>
				<Image
					unoptimized={true}
					src={image.src}
					alt={image.alt}
					priority
					height={image.height}
					width={image.width}
					quality={100}
				/>
			</div>
		</section>
	)
}
